import React from 'react'
import { sumBy } from 'lodash'
import { useQueryClient } from 'react-query'
import {
  useCreateReportGridRow,
  useMoveReportGridRow,
  useDestroyReportGridRowById,
  useUpdateReportGridRow
} from 'api/hooks/use-report-grid-rows'
import { useUpdateReportChart, useDestroyReportChartById } from 'api/hooks/use-report-charts'
import ReportGridRow from './grid-row'
import AddGridRow from '../dashboard/add-grid-row'
import CardLoading from 'shared/card-loading'
import CardError from 'shared/card-error'

const GRID_COLUMN_LIMIT = 60

function ReportGrid({
  reportId,
  gridRows = [],
  datasets = [],
  reportGroupingId = null,
  canEdit = false,
  isPreviewMode = false,
  selectedChartId = null,
  onSelectChart = null,
  selectedRowId = null,
  onSelectRow = null,
  selectedEmptySlot = null,
  onSelectEmptySlot = null,
  isLoading = false,
  isError = false,
  onRetry = null,
  isRetrying = false
}) {
  const totalColumnCount = sumBy(gridRows, 'column_count')
  const remainingColumnCapacity = GRID_COLUMN_LIMIT - totalColumnCount

  const { mutate: createGridRow, isLoading: isCreatingGridRow } = useCreateReportGridRow({
    reportId,
    options: {}
  })

  const { mutate: moveGridRow } = useMoveReportGridRow({ reportId, options: {} })
  const { mutate: destroyGridRow } = useDestroyReportGridRowById({ reportId, options: {
    onSuccess: () => onSelectRow?.(null)
  }})

  const queryClient = useQueryClient()
  const { mutateAsync: updateGridRowAsync } = useUpdateReportGridRow({ reportId, options: {} })
  const { mutateAsync: updateChartAsync } = useUpdateReportChart({ options: {} })
  const { mutateAsync: destroyChartAsync } = useDestroyReportChartById({ options: {} })

  const handleAddRow = (columnCount) => createGridRow({ column_count: columnCount })

  // Remove a single column from a row (rather than the whole row): drop the chart in that column
  // if any, slide the columns to its right left by one to close the gap, then shrink the row's
  // column_count. Deleting a row's only remaining column falls back to removing the row.
  const handleDeleteColumn = async (gridRow, position) => {
    if (gridRow.column_count <= 1) {
      destroyGridRow(gridRow.id)
      return
    }
    const charts = gridRow.report_charts || []
    const chartInColumn = charts.find((c) => c.grid_column_position === position)
    const toShift = charts
      .filter((c) => c.grid_column_position > position)
      .sort((a, b) => a.grid_column_position - b.grid_column_position)
    try {
      if (chartInColumn) await destroyChartAsync(chartInColumn.id)
      for (const c of toShift) {
        await updateChartAsync({ id: c.id, grid_column_position: c.grid_column_position - 1 })
      }
      await updateGridRowAsync({ id: gridRow.id, column_count: gridRow.column_count - 1 })
    } finally {
      queryClient.invalidateQueries([`reports/${reportId}/report_grid_rows`])
    }
  }

  const handleMoveUp = (gridRow) => {
    const newPosition = Math.max(0, gridRow.row_position - 1)
    moveGridRow({ id: gridRow.id, new_position: newPosition })
  }

  const handleMoveDown = (gridRow) => {
    const newPosition = Math.min(gridRows.length - 1, gridRow.row_position + 1)
    moveGridRow({ id: gridRow.id, new_position: newPosition })
  }

  if (isError) {
    return (
      <CardError
        title="Couldn't load this report"
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    )
  }

  if (isLoading) return <CardLoading label="Loading report…" />

  return (
    <div>
      {gridRows.map((gridRow, index) => (
        <ReportGridRow
          key={gridRow.id}
          gridRow={gridRow}
          reportId={reportId}
          datasets={datasets}
          reportGroupingId={reportGroupingId}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onDeleteRow={(row) => destroyGridRow(row.id)}
          onDeleteColumn={handleDeleteColumn}
          isFirst={index === 0}
          isLast={index === gridRows.length - 1}
          canEdit={canEdit}
          isPreviewMode={isPreviewMode}
          selectedChartId={selectedChartId}
          onSelectChart={onSelectChart}
          isRowSelected={selectedRowId === gridRow.id}
          onSelectRow={onSelectRow}
          selectedEmptySlot={selectedEmptySlot}
          onSelectEmptySlot={onSelectEmptySlot}
        />
      ))}

      {canEdit && !isPreviewMode && (
        <AddGridRow
          onAddRow={handleAddRow}
          isLoading={isCreatingGridRow}
          remainingCapacity={remainingColumnCapacity}
          totalColumnCount={totalColumnCount}
        />
      )}
    </div>
  )
}

export default ReportGrid
